import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { Broadcast, ChatCircle, CheckSquare, ArrowUpRight, MapPin, Camera, Copy, Wrench, ArrowsClockwise, Clock, LinkSimple, MagnifyingGlass, X, CheckCircle, DotsThreeVertical, Trash, PencilSimple } from "@phosphor-icons/react";

/**
 * AdminLiveFeed — unified real-time stream of Kraaft (via Make.com) and
 * ClickUp (via native webhook) events. Auto-polls every 5s.
 */
export default function AdminLiveFeed() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ kraaft: 0, clickup: 0 });
  const [filter, setFilter] = useState("all"); // all | kraaft | clickup
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [rotating, setRotating] = useState(false);
  const lastFingerprint = useRef("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/kraaft/live-feed", { params: { limit: 60 } });
      const next = r.data?.items || [];
      const fp = next.map(x => `${x.source}:${x.id}`).join("|");
      if (fp !== lastFingerprint.current) {
        setItems(next);
        setCounts(r.data?.counts || { kraaft: 0, clickup: 0 });
        lastFingerprint.current = fp;
      }
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    // Bookmark this visit so the sidebar red-dot clears
    api.post("/kraaft/live-feed/mark-seen", {}).catch(() => {});
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const rotate = async () => {
    setRotating(true);
    try {
      const r = await api.post("/kraaft/make-webhook-secret/rotate", {});
      setWebhookInfo(r.data);
      toast.success("New Make.com webhook URL minted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not rotate"));
    } finally { setRotating(false); }
  };

  const filtered = items.filter(x => {
    if (filter === "all") return true;
    if (filter === "kraaft") return x.source === "kraaft" || x.source === "kraaft_mirror";
    return x.source === "clickup";
  });

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-12 w-12 bg-gradient-to-br from-red-500 to-fuchsia-600 rounded-lg flex items-center justify-center border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a] relative">
            <Broadcast weight="fill" size={26} className="text-white"/>
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-pulse border border-white"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-3xl uppercase tracking-tight leading-none flex items-center gap-2">
              Live Feed
              <span className="text-xs font-black uppercase tracking-widest bg-red-600 text-white px-2 py-0.5 rounded animate-pulse">● LIVE</span>
            </div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">
              War-room stream · Kraaft {counts.kraaft} · ClickUp {counts.clickup} · refreshes every 5s
            </div>
          </div>
          <button onClick={load} data-testid="live-refresh"
                  className="h-10 w-10 border-2 border-slate-900 bg-white flex items-center justify-center hover:bg-slate-100">
            <ArrowsClockwise size={18} weight="bold"/>
          </button>
        </div>

        {/* Filters */}
        <div className="mb-4 flex gap-2">
          {["all", "kraaft", "clickup"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`live-filter-${f}`}
              className={`h-9 px-4 border-2 rounded-md font-black uppercase tracking-widest text-xs ${
                filter === f
                  ? (f === "kraaft" ? "border-fuchsia-500 bg-fuchsia-500 text-white shadow-[2px_2px_0px_#0f172a]" :
                     f === "clickup" ? "border-cyan-500 bg-cyan-500 text-white shadow-[2px_2px_0px_#0f172a]" :
                     "border-slate-900 bg-slate-900 text-white shadow-[2px_2px_0px_#0f172a]")
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
              }`}>
              {f === "all" ? "All" : f === "kraaft" ? "Kraaft" : "ClickUp"}
            </button>
          ))}
        </div>

        {/* Make.com webhook helper */}
        <MakeSetupCard webhookInfo={webhookInfo} onRotate={rotate} rotating={rotating}/>

        {/* Unmatched Kraaft records — office hand-link rescue */}
        <UnmatchedRescueCard/>

        {/* Feed */}
        <div className="bg-white border-2 border-slate-900 rounded-lg shadow-[3px_3px_0px_#0f172a] overflow-hidden">
          <div className="px-4 py-2 border-b-2 border-slate-900 bg-slate-100 flex items-center gap-2">
            <Clock weight="fill" size={14} className="text-slate-600"/>
            <div className="text-xs font-black uppercase tracking-widest text-slate-700">Recent activity · {filtered.length}</div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-200">
            {filtered.length === 0 && (
              <div className="p-8 text-center">
                <Broadcast weight="light" size={40} className="mx-auto text-slate-300"/>
                <div className="text-sm font-bold text-slate-500 mt-2">Waiting for events…</div>
                <div className="text-xs text-slate-400 mt-1">
                  Kraaft events arrive via your Make.com scenario · ClickUp events arrive via native webhook
                </div>
              </div>
            )}
            {filtered.map(it => <FeedRow key={`${it.source}-${it.id}`} it={it} onChange={load}/>)}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

function FeedRow({ it, onChange }) {
  const isKraaft = it.source === "kraaft" || it.source === "kraaft_mirror";
  const isKraaftMirror = it.source === "kraaft_mirror"; // has record_id for actions
  const [menuOpen, setMenuOpen] = useState(false);
  const [relinkOpen, setRelinkOpen] = useState(false);
  const chipLabel = isKraaft ? "K" : "CU";
  const chipColor = isKraaft ? "bg-fuchsia-600" : "bg-cyan-600";
  const kindLabel = it.kind === "comment" ? "Comment" :
                    it.kind === "assign" ? "Assigned" :
                    it.kind === "status" ? "Status" :
                    it.kind === "backfill" ? "Kraaft record" :
                    it.kind === "record.created" ? "New record" :
                    it.kind === "record.updated" ? "Updated" :
                    it.kind;
  const Icon = it.kind === "comment" ? ChatCircle :
               it.kind === "assign" ? Wrench :
               it.kind === "status" ? CheckSquare :
               Broadcast;
  const when = it.at ? new Date(it.at) : null;
  const ago = when ? _timeAgo(when) : "";

  // Kraaft mirror row has stable record_id at end of composite _id
  const recordId = isKraaftMirror && it.id ? String(it.id).split(":").pop() : null;

  const dismiss = async (e) => {
    e?.stopPropagation();
    setMenuOpen(false);
    if (!recordId) return;
    if (!window.confirm("Hide this record from the feed?")) return;
    try {
      await api.post(`/kraaft/records/${recordId}/dismiss`);
      toast.success("Hidden");
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Could not dismiss"));
    }
  };
  const hardDelete = async (e) => {
    e?.stopPropagation();
    setMenuOpen(false);
    if (!recordId) return;
    if (!window.confirm("Delete this mirrored record? Kraaft itself is untouched — this only removes it from Field Radar.")) return;
    try {
      await api.delete(`/kraaft/records/${recordId}`);
      toast.success("Deleted");
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Could not delete"));
    }
  };
  const openRelink = (e) => {
    e?.stopPropagation();
    setMenuOpen(false);
    setRelinkOpen(true);
  };

  const canDeepLink = !!it.matched_job_id;
  const RowWrapper = ({ children }) => canDeepLink
    ? <Link to={`/admin/jobs/${it.matched_job_id}`} className="block" data-testid={`live-row-link-${it.id}`}>{children}</Link>
    : <div>{children}</div>;

  return (
    <>
      <RowWrapper>
        <div className="p-3 hover:bg-slate-50 transition-colors relative cursor-pointer" data-testid={`live-item-${it.id}`}>
          <div className="flex items-start gap-3">
            <div className={`h-8 w-8 flex-shrink-0 rounded ${chipColor} text-white text-[10px] font-black grid place-content-center border-2 border-slate-900 shadow-[2px_2px_0px_#0f172a]`}>
              {chipLabel}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <Icon weight="bold" size={12} className="text-slate-500 flex-shrink-0"/>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">{kindLabel}</span>
                {it.actor && <span className="text-[10px] text-slate-500">· by <b>{it.actor}</b></span>}
                {it.matched_job_id ? (
                  <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                    → Matched WO
                  </span>
                ) : (
                  <span className="text-[10px] font-black uppercase tracking-widest bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                    unmatched
                  </span>
                )}
                <span className="ml-auto text-[10px] font-bold text-slate-400 flex-shrink-0">{ago}</span>
              </div>
              {it.title && <div className="text-sm font-black text-slate-900 truncate">{it.title}</div>}
              {it.preview && <div className="text-xs text-slate-600 line-clamp-2 mt-0.5">{it.preview}</div>}
              <div className="mt-1 flex gap-2 items-center">
                {it.photo_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-800 px-1.5 py-0.5 rounded">
                    <Camera weight="fill" size={10}/> {it.photo_count}
                  </span>
                )}
                {it.kraaft_deeplink && (
                  <a href={it.kraaft_deeplink} target="_blank" rel="noopener noreferrer"
                     onClick={(e) => e.stopPropagation()}
                     className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:text-fuchsia-900">
                    <ArrowUpRight weight="bold" size={12}/> Kraaft
                  </a>
                )}
              </div>
            </div>
            {isKraaftMirror && recordId && (
              <div className="flex-shrink-0 relative">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(v => !v); }}
                  data-testid={`live-row-menu-${it.id}`}
                  className="h-8 w-8 flex items-center justify-center border border-slate-300 bg-white hover:bg-slate-100 rounded">
                  <DotsThreeVertical weight="bold" size={16}/>
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); }}/>
                    <div className="absolute right-0 top-9 w-44 bg-white border-2 border-slate-900 rounded shadow-[3px_3px_0px_#0f172a] z-50 overflow-hidden"
                         onClick={(e) => e.stopPropagation()}>
                      <button onClick={openRelink} data-testid={`live-row-relink-${it.id}`}
                              className="w-full px-3 py-2 text-left text-xs font-black uppercase tracking-widest hover:bg-cyan-50 flex items-center gap-2">
                        <PencilSimple weight="bold" size={12}/> Change WO
                      </button>
                      <button onClick={dismiss} data-testid={`live-row-dismiss-${it.id}`}
                              className="w-full px-3 py-2 text-left text-xs font-black uppercase tracking-widest hover:bg-amber-50 border-t border-slate-100 flex items-center gap-2">
                        <X weight="bold" size={12}/> Hide from feed
                      </button>
                      <button onClick={hardDelete} data-testid={`live-row-delete-${it.id}`}
                              className="w-full px-3 py-2 text-left text-xs font-black uppercase tracking-widest text-red-700 hover:bg-red-50 border-t border-slate-100 flex items-center gap-2">
                        <Trash weight="bold" size={12}/> Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </RowWrapper>
      {relinkOpen && recordId && (
        <HandLinkModal
          record={{ record_id: recordId, title: it.title, notes: it.preview }}
          onClose={() => setRelinkOpen(false)}
          onDone={async () => { setRelinkOpen(false); onChange?.(); }}
          isRelink={true}
        />
      )}
    </>
  );
}

function MakeSetupCard({ webhookInfo, onRotate, rotating }) {
  const [showRecipe, setShowRecipe] = useState(false);
  return (
    <div className="mb-4 rounded-lg border-2 border-fuchsia-300 bg-fuchsia-50/50 p-3" data-testid="make-setup-card">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-6 w-6 rounded bg-fuchsia-600 text-white text-[10px] font-black grid place-content-center">M</div>
        <div className="text-xs font-black uppercase tracking-widest text-fuchsia-900">Make.com bridge · plug Kraaft into this feed</div>
        <button onClick={() => setShowRecipe(v => !v)} data-testid="make-toggle-recipe"
                className="ml-auto h-7 px-2 border-2 border-fuchsia-700 bg-white hover:bg-fuchsia-100 text-fuchsia-800 text-[10px] font-black uppercase tracking-widest rounded">
          {showRecipe ? "Hide recipe" : "Show recipe"}
        </button>
      </div>
      {!webhookInfo && (
        <button onClick={onRotate} disabled={rotating} data-testid="make-mint-btn"
                className="h-10 px-4 bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-50 text-white font-black uppercase tracking-widest text-xs border-2 border-slate-900 shadow-[2px_2px_0px_#0f172a] rounded-md">
          {rotating ? "Minting…" : "🪄 Mint webhook URL for Make"}
        </button>
      )}
      {webhookInfo && (
        <div className="space-y-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-800">Paste this URL into Make's HTTP module:</div>
          <div className="flex gap-2">
            <input readOnly value={webhookInfo.url} data-testid="make-webhook-url"
                   className="flex-1 h-10 px-3 border-2 border-slate-300 rounded-md font-mono text-xs bg-white"/>
            <button onClick={() => { navigator.clipboard.writeText(webhookInfo.url); toast.success("Copied"); }}
                    data-testid="make-webhook-copy"
                    className="h-10 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 text-slate-900 flex items-center gap-1 rounded-md text-xs font-black uppercase tracking-widest">
              <Copy weight="bold" size={14}/> Copy
            </button>
            <button onClick={onRotate} disabled={rotating} data-testid="make-webhook-rotate"
                    className="h-10 px-3 border-2 border-red-500 bg-white hover:bg-red-50 text-red-700 rounded-md text-xs font-black uppercase tracking-widest">
              {rotating ? "…" : "Rotate"}
            </button>
          </div>
          <div className="text-[10px] text-slate-500">Secret hint: <span className="font-mono">{webhookInfo.secret_hint}</span></div>
        </div>
      )}
      {showRecipe && (
        <div className="mt-3 pt-3 border-t border-fuchsia-200 text-xs text-slate-700 space-y-2">
          <div className="font-black uppercase tracking-widest text-[10px] text-fuchsia-800">Make.com scenario recipe · 4 steps</div>
          <ol className="list-decimal list-inside space-y-1.5 text-slate-700">
            <li>Log into Make.com → <b>Create a new scenario</b>.</li>
            <li>Add a trigger module: <b>Kraaft › Watch Events</b> (or <b>Watch Records</b>). Connect it to your Kraaft workspace (Make handles OAuth).</li>
            <li>Add an HTTP module: <b>Make a request</b>. Method <span className="font-mono">POST</span>. URL: the one above. Body type <span className="font-mono">application/json</span>. Body: pass the trigger's whole event object as-is.</li>
            <li>Turn scenario ON. Every Kraaft record change now lands here within ~5-15s.</li>
          </ol>
          <div className="text-[10px] text-slate-500 mt-2">Notes: We dedupe by <span className="font-mono">event_id</span> so Make can safely retry. The URL contains a secret — if you leak it, tap <b>Rotate</b> to invalidate.</div>
        </div>
      )}
    </div>
  );
}

function _timeAgo(d) {
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

// ── Unmatched rescue ─────────────────────────────────────────────────

function UnmatchedRescueCard() {
  const [records, setRecords] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null); // record being linked
  const load = useCallback(async () => {
    try {
      const r = await api.get("/kraaft/records/unmatched?limit=100");
      setRecords(r.data?.records || []);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (records.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border-2 border-amber-400 bg-amber-50/60 p-3" data-testid="unmatched-rescue-card">
      <div className="flex items-center gap-2">
        <div className="h-6 w-6 rounded bg-amber-500 text-slate-900 text-[10px] font-black grid place-content-center border-2 border-slate-900">!</div>
        <div className="text-xs font-black uppercase tracking-widest text-amber-900 flex-1">
          {records.length} unmatched Kraaft record{records.length === 1 ? "" : "s"} · hand-link to rescue
        </div>
        <button onClick={() => setExpanded(v => !v)} data-testid="unmatched-toggle"
                className="h-7 px-2 border-2 border-amber-700 bg-white hover:bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-widest rounded">
          {expanded ? "Hide" : "Show"}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
          {records.map(r => (
            <div key={r._id} data-testid={`unmatched-row-${r.record_id}`}
                 className="border border-amber-300 bg-white rounded p-2 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                {r.title && <div className="text-sm font-black text-slate-900 truncate">{r.title}</div>}
                {r.notes && <div className="text-xs text-slate-600 line-clamp-2">{r.notes}</div>}
                <div className="mt-1 flex gap-1.5 items-center flex-wrap">
                  {(r.kraaft_parcel_count || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-800 px-1.5 py-0.5 rounded">
                      <Camera weight="fill" size={10}/> {r.kraaft_parcel_count}
                    </span>
                  )}
                  {r.kraaft_geo && (
                    <a href={`https://www.google.com/maps/search/?api=1&query=${r.kraaft_geo.lat},${r.kraaft_geo.lng}`}
                       target="_blank" rel="noopener noreferrer"
                       className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-cyan-700 inline-flex items-center gap-1">
                      <MapPin weight="fill" size={11}/> pin
                    </a>
                  )}
                  {r.kraaft_deeplink && (
                    <a href={r.kraaft_deeplink} target="_blank" rel="noopener noreferrer"
                       className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:text-fuchsia-900 inline-flex items-center gap-1">
                      <ArrowUpRight weight="bold" size={11}/> Kraaft
                    </a>
                  )}
                </div>
              </div>
              <button
                onClick={() => setLinkTarget(r)}
                data-testid={`unmatched-link-btn-${r.record_id}`}
                className="h-9 px-3 border-2 border-slate-900 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black uppercase tracking-widest text-[10px] rounded shadow-[2px_2px_0px_#0f172a] inline-flex items-center gap-1 flex-shrink-0">
                <LinkSimple weight="bold" size={12}/> Link
              </button>
            </div>
          ))}
        </div>
      )}
      {linkTarget && (
        <HandLinkModal
          record={linkTarget}
          onClose={() => setLinkTarget(null)}
          onDone={() => { setLinkTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function HandLinkModal({ record, onClose, onDone, isRelink = false }) {
  const [query, setQuery] = useState(_bestGuessQuery(record));
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(null);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const runSearch = useCallback(async (q) => {
    if ((q || "").trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.post("/kraaft/records/wo-search", { query: q });
      setResults(r.data?.jobs || []);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced auto-search
  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const link = async (jobId) => {
    setLinking(jobId);
    try {
      if (isRelink) {
        await api.post(`/kraaft/records/${record.record_id}/relink`, { job_id: jobId });
        toast.success("Moved to new WO");
      } else {
        await api.post("/kraaft/records/hand-link", {
          record_id: record.record_id, job_id: jobId,
        });
        toast.success("Linked · atlas seeded for future auto-matches");
      }
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not link"));
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
         data-testid="hand-link-modal" onClick={onClose}>
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-emerald-500 flex items-center gap-2 bg-emerald-50">
          <LinkSimple weight="fill" size={22} className="text-emerald-700"/>
          <div className="font-display font-black text-lg uppercase tracking-tight flex-1 text-emerald-900">{isRelink ? "Change WO for this record" : "Hand-link to a WO"}</div>
          <button onClick={onClose} data-testid="hand-link-close"
                  className="text-slate-500 hover:text-slate-800 h-9 w-9 flex items-center justify-center">
            <X size={20}/>
          </button>
        </div>
        {/* Kraaft record preview */}
        <div className="p-3 bg-slate-50 border-b border-slate-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Kraaft record</div>
          <div className="text-sm font-black text-slate-900">{record.title || "(no title)"}</div>
          {record.notes && <div className="text-xs text-slate-600 line-clamp-2 mt-1">{record.notes}</div>}
        </div>
        {/* Search */}
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input list="known-addresses" autoComplete="off"
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search WO by address, title, or WO#…"
              data-testid="hand-link-search"
              className="w-full h-10 pl-9 pr-3 border-2 border-slate-300 rounded-md text-sm font-medium focus:border-emerald-500 focus:outline-none"
            />
          </div>
        </div>
        {/* Results */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {searching && <div className="p-4 text-center text-xs font-black uppercase tracking-widest text-slate-400">Searching…</div>}
          {!searching && results.length === 0 && query.length >= 2 && (
            <div className="p-8 text-center">
              <div className="text-sm font-black text-slate-500">No WOs match</div>
              <div className="text-xs text-slate-400 mt-1">Try just the street name or WO number</div>
            </div>
          )}
          {results.map(j => (
            <button
              key={j.id}
              onClick={() => link(j.id)}
              disabled={linking === j.id}
              data-testid={`hand-link-pick-${j.id}`}
              className="w-full p-3 flex items-center gap-3 text-left hover:bg-emerald-50 disabled:opacity-50">
              <MapPin weight="fill" size={14} className="text-slate-500 flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black text-slate-900 truncate">{j.property_address || "—"}</div>
                <div className="text-xs text-slate-600 line-clamp-1">{j.title}</div>
                <div className="mt-0.5 flex gap-1.5">
                  {j.wo_no && <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">WO {j.wo_no}</span>}
                  {j.status && <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded">{j.status}</span>}
                </div>
              </div>
              {linking === j.id ? (
                <span className="text-xs font-black text-slate-500">…</span>
              ) : (
                <CheckCircle weight="fill" size={22} className="text-emerald-500 flex-shrink-0"/>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function _bestGuessQuery(record) {
  // Extract [address] from title first
  const t = record.title || "";
  const m = t.match(/\[([^\]]+)\]/);
  if (m) {
    // Grab the numeric prefix + first street token
    const inner = m[1];
    const parts = inner.split(/\s+/);
    return parts.slice(0, 2).join(" ");
  }
  // Fall back to street number
  const sn = t.match(/(\d{2,5}\s+[A-Za-z][A-Za-z]+)/);
  return sn ? sn[1] : "";
}
